import { Box, HStack, IconButton, Image, Textarea } from '@chakra-ui/react'
import { Image as ImageIcon, Send, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useColorModeValue } from '~/color-mode'

interface AiChatInputProps {
  onSend: (message: string, imageBase64?: string, imageMimeType?: string) => void
  isLoading: boolean
  placeholder?: string
}

export function AiChatInput({
  onSend,
  isLoading,
  placeholder = 'Ask AI to help build your architecture...',
}: AiChatInputProps) {
  const [message, setMessage] = useState('')
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageBase64, setImageBase64] = useState<string | null>(null)
  // brand.emphasized is lighter than brand.solid in light mode (yellow.200 < yellow.300), so it
  // washes out on hover instead of darkening. Use an explicit pair that's darker than brand.solid
  // (yellow.300/yellow.500) in both modes: yellow.500/yellow.600.
  const sendButtonHoverBg = useColorModeValue('yellow.500', 'yellow.600')
  const [imageMimeType, setImageMimeType] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleFileSelect = useCallback((file: File) => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      setImagePreview(dataUrl)
      // Extract base64 part (remove data URL prefix)
      const base64 = dataUrl.split(',')[1]
      setImageBase64(base64)
      setImageMimeType(file.type)
    }
    reader.readAsDataURL(file)
  }, [])

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
    e.target.value = '' // Reset for re-selection
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const clearImage = () => {
    setImagePreview(null)
    setImageBase64(null)
    setImageMimeType(null)
  }

  const handleSend = () => {
    if (!message.trim() && !imageBase64) return

    onSend(message.trim(), imageBase64 || undefined, imageMimeType || undefined)

    setMessage('')
    clearImage()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Box
      p={3}
      borderTopWidth="1px"
      bg="bg.panel"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Image Preview */}
      {imagePreview && (
        <Box position="relative" mb={2} display="inline-block">
          <Image
            src={imagePreview}
            alt="Upload preview"
            maxH="100px"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
          />
          <IconButton
            aria-label="Remove image"
            size="xs"
            position="absolute"
            top={-2}
            right={-2}
            borderRadius="full"
            colorPalette="red"
            onClick={clearImage}
          >
            <X size={12} />
          </IconButton>
        </Box>
      )}

      {/* Drag overlay */}
      {isDragging && (
        <Box
          position="absolute"
          inset={0}
          bg="blue.100"
          opacity={0.8}
          borderRadius="md"
          border="2px dashed"
          borderColor="blue.400"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={10}
          pointerEvents="none"
        >
          Drop image here
        </Box>
      )}

      <HStack gap={2} align="flex-end">
        {/* Image upload button */}
        <IconButton
          aria-label="Upload image"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
        >
          <ImageIcon size={18} />
        </IconButton>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {/* Message input */}
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          resize="none"
          minH="40px"
          maxH="120px"
          rows={1}
          flex={1}
          disabled={isLoading}
          _disabled={{ opacity: 0.6 }}
        />

        {/* Send button */}
        <IconButton
          aria-label="Send message"
          colorPalette="yellow"
          bg="brand.solid"
          _hover={{ bg: sendButtonHoverBg }}
          size="sm"
          onClick={handleSend}
          disabled={isLoading || (!message.trim() && !imageBase64)}
          loading={isLoading}
        >
          <Send size={18} />
        </IconButton>
      </HStack>
    </Box>
  )
}

export default AiChatInput
